import React, { useState, useEffect } from 'react';
import { Eye, EyeOff, Lock, User, ArrowLeft } from 'lucide-react';
import OTPMethodSelector from './OTPMethodSelector';
import OTPVerificationModal from './OTPVerificationModal';
import { generateDeviceFingerprint } from '../../utils/deviceFingerprint';
import { API_BASE_URL } from '../../config';

const AdminLogin = ({ onClose, onLoginSuccess, initialError = '' }) => {
  const [step, setStep] = useState('credentials'); // 'credentials', 'method', 'otp', or 'forgot-password'
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(initialError);
  const [requiresPasswordReset, setRequiresPasswordReset] = useState(false);
  const [deviceFingerprint, setDeviceFingerprint] = useState(null);
  const [adminData, setAdminData] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [otpSentTo, setOtpSentTo] = useState('');
  const [otpExpiresAt, setOtpExpiresAt] = useState(null);
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotMessage, setForgotMessage] = useState('');

  // Generate device fingerprint on mount
  useEffect(() => {
    generateDeviceFingerprint().then(fp => {
      setDeviceFingerprint(fp);
    });
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleCredentialsSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setRequiresPasswordReset(false);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          username: formData.username,
          password: formData.password,
          fingerprint: deviceFingerprint
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setAdminData(data);
        setStep('method');
      } else {
        setError(data.error || 'Login failed');
        setRequiresPasswordReset(Boolean(data.requiresPasswordReset));
      }
    } catch (error) {
      console.error('Login error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoToForgotPassword = () => {
    setForgotEmail('');
    setForgotMessage('');
    setError('');
    setStep('forgot-password');
  };

  const handleForgotPasswordSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setForgotMessage('');
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/forgot-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotEmail })
      });
      const data = await response.json();
      setForgotMessage(data.message || 'If an account exists for that email, a password reset link has been sent.');
    } catch (error) {
      console.error('Forgot password error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleMethodSelect = async (method) => {
    setLoading(true);
    setError('');
    setSelectedMethod(method);

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/send-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          adminId: adminData.adminId,
          method: method,
          fingerprint: deviceFingerprint,
          otpToken: adminData.otpToken // Send token as fallback if cookies fail
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setOtpSentTo(data.sentTo);
        setOtpExpiresAt(new Date(data.expiresAt));
        setStep('otp');
      } else {
        setError(data.error || 'Failed to send OTP');
      }
    } catch (error) {
      console.error('OTP send error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    await handleMethodSelect(selectedMethod);
  };

  const handleOTPVerify = async (otpCode) => {
    setLoading(true);
    setError('');

    try {
      const response = await fetch(`${API_BASE_URL}/api/admin/auth/verify-otp`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          adminId: adminData.adminId,
          otp: otpCode,
          fingerprint: deviceFingerprint,
          otpToken: adminData.otpToken // Send token as fallback if cookies fail
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('adminData', JSON.stringify(data.admin));
        
        // Show success notification
        const notification = document.createElement('div');
        notification.className = 'fixed top-4 right-4 bg-green-500 text-white px-6 py-3 rounded-lg shadow-lg z-[100] transform transition-all duration-300 translate-x-full';
        notification.innerHTML = `
          <div class="flex items-center">
            <svg class="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clip-rule="evenodd"></path>
            </svg>
            <span>Admin login successful! Redirecting to dashboard...</span>
          </div>
        `;
        document.body.appendChild(notification);

        setTimeout(() => {
          notification.classList.remove('translate-x-full');
        }, 100);

        setTimeout(() => {
          onLoginSuccess(data.admin);
          notification.classList.add('translate-x-full');
          setTimeout(() => {
            document.body.removeChild(notification);
          }, 300);
        }, 1500);
      } else {
        setError(data.error || 'OTP verification failed');
      }
    } catch (error) {
      console.error('OTP verification error:', error);
      setError('Network error. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToCredentials = () => {
    setStep('credentials');
    setFormData({ username: '', password: '' });
    setAdminData(null);
    setSelectedMethod(null);
    setOtpSentTo('');
    setError('');
    setRequiresPasswordReset(false);
  };

  const handleBackToMethod = () => {
    setStep('method');
    setError('');
  };

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur-sm flex items-center justify-center z-[90] p-4">
      <div className="bg-surface rounded-md shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 bg-ink text-paper rounded-t-md">
          <div className="flex items-center">
            {(step === 'otp' || step === 'method' || step === 'forgot-password') && (
              <button
                onClick={step === 'otp' ? handleBackToMethod : handleBackToCredentials}
                className="mr-2 p-2 hover:bg-paper/10 rounded-full transition-colors"
                aria-label="Back"
              >
                <ArrowLeft className="h-5 w-5 text-paper" />
              </button>
            )}
            <h2 className="font-display text-xl text-paper">
              {step === 'credentials' && 'Admin Login'}
              {step === 'method' && 'Choose Verification Method'}
              {step === 'otp' && 'Verify OTP'}
              {step === 'forgot-password' && 'Reset Password'}
            </h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 text-paper/80 hover:text-paper hover:bg-paper/10 rounded-full transition-colors"
            aria-label="Close"
          >
            <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {step === 'credentials' ? (
            <form onSubmit={handleCredentialsSubmit} className="space-y-4">
              <div>
                <label className="dpc-label text-ink-soft block mb-1.5">
                  Username or Email
                </label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ink-faint" />
                  <input
                    type="text"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-4 py-3 border border-ink-faint rounded-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent transition-colors duration-200"
                    placeholder="Enter username or email"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="dpc-label text-ink-soft block mb-1.5">
                  Password
                </label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-ink-faint" />
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="w-full pl-10 pr-12 py-3 border border-ink-faint rounded-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent transition-colors duration-200"
                    placeholder="Enter password"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 text-ink-faint hover:text-ink"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="bg-accent-soft border border-accent/30 text-accent-deep px-4 py-3 rounded-sm text-sm">
                  {error}
                  {requiresPasswordReset && (
                    <button
                      type="button"
                      onClick={handleGoToForgotPassword}
                      className="block mt-1.5 font-medium underline hover:no-underline"
                    >
                      Reset your password
                    </button>
                  )}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-accent text-paper py-3 px-4 rounded-full hover:bg-accent-deep focus:ring-2 focus:ring-accent focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 shadow-md hover:shadow-lg dpc-label"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-paper mr-2"></div>
                    Signing In...
                  </div>
                ) : (
                  'Sign In'
                )}
              </button>

              <div className="text-center">
                <button
                  type="button"
                  onClick={handleGoToForgotPassword}
                  className="text-accent hover:text-accent-deep text-sm font-medium transition-colors"
                >
                  Forgot password?
                </button>
              </div>
            </form>
          ) : step === 'forgot-password' ? (
            <div className="space-y-4">
              {forgotMessage ? (
                <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-sm text-sm">
                  {forgotMessage}
                </div>
              ) : (
                <form onSubmit={handleForgotPasswordSubmit} className="space-y-4">
                  <p className="text-sm text-ink-soft">Enter your account email and we'll send you a link to reset your password.</p>
                  <div>
                    <label className="dpc-label text-ink-soft block mb-1.5">Email</label>
                    <input
                      type="email"
                      value={forgotEmail}
                      onChange={(e) => setForgotEmail(e.target.value)}
                      className="w-full px-4 py-3 border border-ink-faint rounded-sm bg-surface focus:ring-1 focus:ring-accent focus:border-accent transition-colors duration-200"
                      placeholder="you@example.com"
                      required
                    />
                  </div>
                  {error && (
                    <div className="bg-accent-soft border border-accent/30 text-accent-deep px-4 py-3 rounded-sm text-sm">
                      {error}
                    </div>
                  )}
                  <button
                    type="submit"
                    disabled={loading}
                    className="w-full bg-accent text-paper py-3 px-4 rounded-full hover:bg-accent-deep disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200 dpc-label"
                  >
                    {loading ? 'Sending…' : 'Send Reset Link'}
                  </button>
                </form>
              )}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  className="text-accent hover:text-accent-deep text-sm font-medium transition-colors"
                >
                  Back to login
                </button>
              </div>
            </div>
          ) : step === 'method' ? (
            <div className="space-y-4">
              <OTPMethodSelector
                availableMethods={adminData?.availableMethods || []}
                preferredMethod={adminData?.preferredMethod || 'SMS'}
                onSelect={handleMethodSelect}
                loading={loading}
                phoneNumber={adminData?.phoneNumber}
                email={adminData?.email}
              />
              {error && (
                <div className="bg-accent-soft border border-accent/30 text-accent-deep px-4 py-3 rounded-sm text-sm">
                  {error}
                </div>
              )}
              <div className="text-center">
                <button
                  type="button"
                  onClick={handleBackToCredentials}
                  className="text-accent hover:text-accent-deep text-sm font-medium transition-colors"
                >
                  Back to login
                </button>
              </div>
            </div>
          ) : (
            <OTPVerificationModal
              method={selectedMethod}
              sentTo={otpSentTo}
              expiresAt={otpExpiresAt}
              onVerify={handleOTPVerify}
              onResend={handleResendOTP}
              onBack={handleBackToMethod}
              loading={loading}
              error={error}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
